import React, { useState, useEffect } from "react";
import { Button, Space, Divider, Typography, theme, Tooltip, Popover, Input } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, SettingOutlined, ReloadOutlined, ExportOutlined, EditOutlined } from "@ant-design/icons";
import { ITicket, TicketStatus } from "../../../Services/TicketServices";
import { getStatusTag, getPriorityTag } from "./utils";

const { Text } = Typography;

const CapsiTaskIconButton: React.FC<{
  ticket: ITicket | null;
  loading: boolean;
  onShiftToCapsiTask?: () => void;
}> = ({ ticket, loading, onShiftToCapsiTask }) => {
  const { token } = theme.useToken();
  const [imgSrc, setImgSrc] = React.useState("/capsitask-icon.svg");

  const isShifted = !!ticket?.capsiTaskRef?.id;

  const iconElement = (
    <img
      src={imgSrc}
      alt="CapsiTask"
      style={{ width: 18, height: 18, objectFit: "contain", verticalAlign: "middle" }}
      onError={() => {
        if (imgSrc === "/capsitask-icon.svg") {
          setImgSrc("/capsitask-favicon.svg");
        }
      }}
    />
  );

  if (isShifted) {
    return (
      <Tooltip title={`Open ${ticket.capsiTaskRef?.name} in CapsiTask`}>
        <Button
          type="default"
          icon={iconElement}
          href={`${process.env.REACT_APP_CAPSITASK_URL || "https://appuat.capsitask.com"}/task/${ticket.capsiTaskRef?.id}?uKey=${ticket.capsiTaskRef?.name}&source=board`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            borderColor: "#1677ff",
            color: "#1677ff",
            fontWeight: 600,
            borderRadius: 16,
            padding: "0 12px",
            height: 32,
            background: "rgba(22, 119, 255, 0.04)",
          }}
        >
          <span>{ticket.capsiTaskRef?.name}</span>
          <ExportOutlined style={{ fontSize: 11, marginLeft: 2 }} />
        </Button>
      </Tooltip>
    );
  }

  if (!onShiftToCapsiTask) return null;

  return (
    <Tooltip title="Raise ticket as Capsitask">
      <Button
        shape="circle"
        icon={iconElement}
        onClick={onShiftToCapsiTask}
        disabled={loading || ticket?.status === TicketStatus.Closed}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderColor: token.colorBorder,
        }}
      />
    </Tooltip>
  );
};

interface TicketHeaderProps {
  ticket: ITicket | null;
  id: string | undefined;
  loading: boolean;
  onDownloadPdf: () => void;
  onBack: () => void;
  onOpenProperties?: () => void;
  onRefresh?: () => void;
  refreshing?: boolean;
  onShiftToCapsiTask?: () => void;
}

export const TicketHeader: React.FC<TicketHeaderProps> = ({
  ticket,
  id,
  loading,
  onDownloadPdf,
  onBack,
  onOpenProperties,
  onRefresh,
  refreshing = false,
  onShiftToCapsiTask,
}) => {
  const { token } = theme.useToken();

  const premiumShadow = token.boxShadowTertiary;
  const subtleBorder = `1px solid ${token.colorBorderSecondary}`;

  return (
    <div
      className="ticket-header-container"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 20px",
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusLG,
        border: subtleBorder,
        boxShadow: premiumShadow,
        marginBottom: 16,
      }}
    >
      <Space size="middle" style={{ overflow: "hidden", flex: 1 }}>
        <Button
          type="text"
          shape="circle"
          icon={<ArrowLeftOutlined style={{ fontSize: 14 }} />}
          onClick={onBack}
          style={{ flexShrink: 0 }}
        />
        <div
          className="ticket-header-grid"
          style={{ display: "grid", gridTemplateColumns: "auto auto 1fr", alignItems: "center", gap: "2px 12px", minWidth: 0, flex: 1 }}
        >
          {/* Row 1, Col 1: Ticket Number */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <Text strong style={{ fontSize: 15, margin: 0, color: token.colorTextHeading, whiteSpace: "nowrap" }}>
              Ticket #{ticket?.number}
            </Text>
            {ticket?.reopenedAt && <ReloadOutlined style={{ color: "#0f1011", fontSize: 13, marginLeft: 10, cursor: "pointer" }} />}
          </div>
          {/* Col 2: Vertical Divider spanning 2 rows */}
          {ticket && (
            <Divider 
              type="vertical" 
              style={{ 
                margin: 0, 
                gridRow: "span 2", 
                height: 28, 
                alignSelf: "center" 
              }} 
            />
          )}

          {/* Row 1, Col 3: Tags */}
          {ticket && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {getStatusTag(ticket.status)}
              {getPriorityTag(ticket.priority)}
            </div>
          )}

          {/* Row 2, Col 1: Ticket Title */}
          <Tooltip title={ticket?.title} placement="bottomLeft">
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 300,
                display: "block",
              }}
            >
              {ticket?.title || "Loading ticket details..."}
            </Text>
          </Tooltip>

          {/* Row 2, Col 3: Assigned Info */}
          {ticket && (
            <Text type="secondary" style={{ fontSize: 11, margin: 0, whiteSpace: "nowrap" }}>
              Assigned: <span style={{ fontWeight: 600, color: token.colorText }}>{ticket?.assignedTo?.name || "-"}</span>
            </Text>
          )}
        </div>
      </Space>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 16 }}>
        <Tooltip title="Download PDF report">
          <Button
            shape="circle"
            icon={<FilePdfOutlined />}
            onClick={onDownloadPdf}
            loading={loading}
          />
        </Tooltip>

        {onRefresh && (
          <Tooltip title="Refresh ticket details & threads">
            <Button
              shape="circle"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={onRefresh}
              loading={refreshing}
            />
          </Tooltip>
        )}
        {onOpenProperties && (
          <Tooltip title="Ticket Actions & Properties">
            <Button
              className="properties-toggle-btn"
              shape="circle"
              icon={<SettingOutlined />}
              onClick={onOpenProperties}
            />
          </Tooltip>
        )}

        {/* CapsiTask Small Icon Button */}
        <CapsiTaskIconButton
          ticket={ticket}
          loading={loading}
          onShiftToCapsiTask={onShiftToCapsiTask}
        />
      </div>
    </div>
  );
};

